/**
 * Centralized Admin API HTTP client.
 *
 * All real network calls from feature code flow through `request()` (or the
 * convenience verb helpers below) so that:
 *   - Auth header (MSAL bearer token, scope `api.access`) is attached in one place.
 *   - Base URL / versioning is configured in one place (via `apiPath`).
 *   - Error shapes are uniform.
 *   - Mock <-> real swap is a single flag: `REACT_APP_USE_MOCK_API`.
 *
 * REAL vs MOCK
 * ------------
 * The namespace modules (products, tags, media, ...) each check
 * `isRealApiEnabled()`. The REAL API is the default everywhere: every build
 * hits the `/api/v1/...` BFF endpoints unless mocks are explicitly opted into
 * with `REACT_APP_USE_MOCK_API=true` (offline dev / Storybook only). Mock mode
 * also renders a persistent "MOCK DATA" badge (see BSM/components/
 * MockDataBadge) so nobody mistakes seeded data for the real database.
 *
 * TOKEN PROVIDER
 * --------------
 * `request()` is a plain module function, but the MSAL token lives in the
 * `useAuth` React hook. `App` registers a token provider once auth resolves via
 * `setAuthTokenProvider(getToken)`; the client then acquires a bearer token on
 * demand without being a hook itself.
 */

import { ENVIRONMENT_CONFIG } from "shared/globals";

const NOT_WIRED =
  "shared/api/client: real API is disabled (REACT_APP_USE_MOCK_API is 'true'). " +
  "Namespace modules should return mock data while mock mode is enabled.";

// The Azure Functions define routes as `v1/<resource>` (e.g. v1/products,
// v1/admin/cart) served under the host's `api` route prefix, so the backend
// origin exposes them at `/api/v1/<resource>`. In DEV, API Management fronts
// that origin under a `v1/store` base, so the public URL is
// `dev-api.wwg.support/v1/store/v1/products` (the leading `v1/store` is the
// gateway base; the inner `v1/products` is the operation, forwarded to the
// Function App). Both the gateway base and the local `api` prefix live in
// `apiBase` (see regionConfig), so namespace paths pass through here
// unchanged as `/v1/<resource>` and resolve correctly in every environment.
export function storeRoute(path) {
  return path;
}

// The real BFF is the DEFAULT. Seeded mocks are an explicit opt-in via
// REACT_APP_USE_MOCK_API=true (offline dev / Storybook) so a missing env var
// can never silently serve fake data. Kept as a function (not a const boolean)
// so it reads the value lazily and is easy to stub in tests.
export function isRealApiEnabled() {
  return !isMockApiEnabled();
}

export function isMockApiEnabled() {
  return String(process.env.REACT_APP_USE_MOCK_API).toLowerCase() === "true";
}

let _tokenProvider = null;

// A request fired from a freshly-opened tab (e.g. Order Management's "Edit"
// which window.open()s the product editor) can race auth: the component mounts
// and calls the API before `App` has had a chance to register the token
// provider. Without this, the request goes out with no Authorization header and
// the BFF returns 401. We expose a promise that resolves the moment the
// provider is registered so `request()` can wait for it (bounded by a timeout
// so a genuinely-unauthenticated context still fails fast rather than hanging).
let _resolveTokenProviderReady;
let _tokenProviderReady = new Promise((resolve) => {
  _resolveTokenProviderReady = resolve;
});

/**
 * Register the function used to obtain an MSAL bearer token. Typically wired
 * once from `App` with the `getToken` returned by the auth container.
 *
 * @param {() => Promise<string>} fn Resolves to a raw access token.
 */
export function setAuthTokenProvider(fn) {
  _tokenProvider = fn;
  if (fn && _resolveTokenProviderReady) {
    _resolveTokenProviderReady();
    _resolveTokenProviderReady = null;
  }
}

// Pull the human-readable message out of a BFF error body (`{ error:
// { message } }` or `{ message }`) so UI surfaces can show it instead of the
// raw "409 Conflict — {json}" string.
function extractServerMessage(detail) {
  if (!detail) return null;
  try {
    const parsed = JSON.parse(detail);
    return parsed?.error?.message || parsed?.message || null;
  } catch {
    return null;
  }
}

/**
 * Turn any caught error into text safe to show an admin in a toast/alert.
 * Prefers the backend's own message (the BFF writes human-readable ones like
 * `A product with SKU "X" already exists.`), then falls back to a
 * status-appropriate hint, and never leaks the raw
 * `Admin API PUT /v1/... failed: 500 — {json}` developer string to the UI.
 *
 * @param {Error} err          The caught error (may be undefined).
 * @param {string} fallback    Context-specific fallback, e.g. "Couldn't save the product."
 */
export function errorMessage(
  err,
  fallback = "Something went wrong. Please try again."
) {
  if (!err) return fallback;
  if (err.serverMessage) return err.serverMessage;
  switch (err.status) {
    case 400:
      return `${fallback} The request was rejected as invalid — double-check the highlighted fields.`;
    case 401:
      return "Your sign-in session has expired. Refresh the page to sign in again.";
    case 403:
      return "You don't have permission to do this. Ask an administrator if you think you should.";
    case 404:
      return `${fallback} It may have been deleted by someone else — refresh the page and try again.`;
    case 409:
      return `${fallback} It conflicts with something that already exists.`;
    default:
      break;
  }
  if (err.status >= 500) {
    return `${fallback} The server hit an unexpected error — try again in a moment. If it keeps happening, report it.`;
  }
  const raw = err.message || "";
  // Browser fetch() network failures ("Failed to fetch" / "NetworkError…").
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return `${fallback} The server couldn't be reached — check your connection and try again.`;
  }
  // Developer-facing client strings are never appropriate for the UI.
  if (raw.startsWith("Admin API") || raw.startsWith("shared/api/client")) {
    return fallback;
  }
  return raw || fallback;
}

function toQueryString(query) {
  const parts = [];
  const append = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    // Arrays become repeated keys (?k=a&k=b) so multi-value filters survive the
    // trip; plain objects are JSON-encoded rather than stringifying to the
    // useless "[object Object]".
    if (Array.isArray(value)) {
      value.forEach((v) => append(key, v));
      return;
    }
    const encoded =
      typeof value === "object"
        ? encodeURIComponent(JSON.stringify(value))
        : encodeURIComponent(value);
    parts.push(`${encodeURIComponent(key)}=${encoded}`);
  };
  Object.entries(query || {}).forEach(([key, value]) => append(key, value));
  return parts.join("&");
}

// Resolves a store BFF path against apiBase (permissions/auth go through
// coreApiUrl instead — see shared/@auth/Auth.js).
function storeApiPath(path) {
  const base = ENVIRONMENT_CONFIG.apiBase;
  const baseWithSlash = base.endsWith("/") ? base : base + "/";
  if (/^https?:\/\//.test(path)) return path;
  return baseWithSlash + (path.startsWith("/") ? path.slice(1) : path);
}

/**
 * Perform an authenticated JSON request against the Admin API.
 *
 * @param {string} path   Path relative to the API base, e.g. "/v1/products".
 * @param {object} [opts] { method, body, query, headers, signal }.
 * @returns {Promise<any>} Parsed JSON response (null for 204).
 */
export async function request(path, opts = {}) {
  if (!isRealApiEnabled()) {
    throw new Error(NOT_WIRED);
  }

  const { method = "GET", body, query, headers = {}, signal } = opts;

  let url = storeApiPath(storeRoute(path));
  const qs = toQueryString(query);
  if (qs) {
    url += (url.includes("?") ? "&" : "?") + qs;
  }

  const finalHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
  };

  if (!_tokenProvider) {
    // Wait briefly for `App` to register the token provider (see
    // _tokenProviderReady above). Resolves immediately once auth is ready in
    // the normal case; the timeout keeps a truly unauthenticated context from
    // hanging forever.
    await Promise.race([
      _tokenProviderReady,
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);
  }

  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    signal,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {
      detail = "";
    }
    const error = new Error(
      `Admin API ${method} ${path} failed: ${res.status} ${res.statusText}` +
        (detail ? ` — ${detail}` : "")
    );
    error.status = res.status;
    error.serverMessage = extractServerMessage(detail);
    throw error;
  }

  if (res.status === 204) return null;

  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    const parseError = new Error(
      `Admin API ${method} ${path} returned a 200 with a non-JSON body`
    );
    parseError.status = res.status;
    throw parseError;
  }
}

export const get = (path, opts) => request(path, { ...opts, method: "GET" });
export const post = (path, body, opts) =>
  request(path, { ...opts, method: "POST", body });
export const put = (path, body, opts) =>
  request(path, { ...opts, method: "PUT", body });
export const patch = (path, body, opts) =>
  request(path, { ...opts, method: "PATCH", body });
export const del = (path, opts) => request(path, { ...opts, method: "DELETE" });

/**
 * Upload a file via multipart/form-data. FormData sets its own Content-Type
 * (with boundary), so we skip the JSON serialisation path.
 */
export async function uploadFile(path, formData) {
  if (!isRealApiEnabled()) {
    throw new Error(NOT_WIRED);
  }

  let url = storeApiPath(storeRoute(path));

  if (!_tokenProvider) {
    await Promise.race([
      _tokenProviderReady,
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);
  }

  const headers = { Accept: "application/json" };
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {}
    const error = new Error(
      `Admin API POST ${path} failed: ${res.status} ${res.statusText}` +
        (detail ? ` — ${detail}` : "")
    );
    error.status = res.status;
    error.serverMessage = extractServerMessage(detail);
    throw error;
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Small helper used by namespace mock functions to mimic an async API call
 * without timer flakiness in tests. Resolves on the next microtask with a
 * structured-cloned copy so callers can mutate freely.
 */
export function mock(value) {
  return Promise.resolve(JSON.parse(JSON.stringify(value)));
}

export default {
  request,
  get,
  post,
  put,
  patch,
  del,
  uploadFile,
  mock,
  isRealApiEnabled,
  isMockApiEnabled,
  errorMessage,
  setAuthTokenProvider,
};
